import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, MapPin, Clock, Send, Phone, Twitter, Linkedin, Facebook } from "lucide-react";

export default function Contact() {
  const { settings } = useOutletContext();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! Your message has been sent to our editorial office.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const email = settings?.contact_email || "editorial@noventrapress.org";
  const title = settings?.contact_title || "Contact Us";
  const subtitle = settings?.contact_subtitle || "Questions about submissions, peer review or your account? Our editorial office is here to help.";
  const address = settings?.contact_address || "Noventra Academic Press\n1 Scholars Way, Cambridge";
  const responseTime = settings?.contact_response_time || "Within 2 business days";
  const note = settings?.contact_note || "For manuscript status enquiries, please include your Submission ID (found in your dashboard) so we can assist you faster.";
  const phone = settings?.contact_phone || "";
  const mapQuery = settings?.contact_map_query || address;
  const socials = [
    { name: "Twitter", url: settings?.social_twitter, Icon: Twitter },
    { name: "LinkedIn", url: settings?.social_linkedin, Icon: Linkedin },
    { name: "Facebook", url: settings?.social_facebook, Icon: Facebook },
  ].filter((s) => s.url);

  return (
    <div>
      <PageHeader eyebrow="Get in Touch" title={title} subtitle={subtitle} breadcrumb="Contact" />

      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1fr_360px] gap-10">
        <form onSubmit={submit} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={form.name} onChange={set("name")} required className="mt-1.5" data-testid="contact-name" placeholder="Your full name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required className="mt-1.5" data-testid="contact-email" placeholder="you@institution.edu" /></div>
          </div>
          <div><Label>Subject</Label><Input value={form.subject} onChange={set("subject")} className="mt-1.5" data-testid="contact-subject" placeholder="How can we help?" /></div>
          <div><Label>Message</Label><Textarea value={form.message} onChange={set("message")} required rows={6} className="mt-1.5" data-testid="contact-message" placeholder="Write your message…" /></div>
          <Button type="submit" disabled={sending} className="rounded-full gap-2" data-testid="contact-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Message</>}
          </Button>
        </form>

        <aside className="space-y-5">
          <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100 mb-4">Editorial Office</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3"><Mail className="w-5 h-5 text-amber-600 mt-0.5" /><div><p className="text-slate-500">Email</p><a href={`mailto:${email}`} className="font-medium text-primary dark:text-sky-400 hover:underline">{email}</a></div></li>
              <li className="flex items-start gap-3" data-testid="contact-address"><MapPin className="w-5 h-5 text-amber-600 mt-0.5" /><div><p className="text-slate-500">Address</p><p className="font-medium text-slate-900 dark:text-slate-200 whitespace-pre-line">{address}</p></div></li>
              <li className="flex items-start gap-3" data-testid="contact-response-time"><Clock className="w-5 h-5 text-amber-600 mt-0.5" /><div><p className="text-slate-500">Response time</p><p className="font-medium text-slate-900 dark:text-slate-200">{responseTime}</p></div></li>
              {phone && (
                <li className="flex items-start gap-3" data-testid="contact-phone"><Phone className="w-5 h-5 text-amber-600 mt-0.5" /><div><p className="text-slate-500">Phone</p><a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="font-medium text-primary dark:text-sky-400 hover:underline">{phone}</a></div></li>
              )}
            </ul>
            {socials.length > 0 && (
              <div className="flex gap-3 mt-5 pt-5 border-t border-slate-100 dark:border-slate-800" data-testid="contact-socials">
                {socials.map(({ name, url, Icon }) => (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer" title={name} data-testid={`contact-social-${name.toLowerCase()}`} className="grid place-items-center w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-amber-500 hover:text-white transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {mapQuery && (
            <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="contact-map">
              <iframe
                title="Editorial office location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                className="w-full h-52 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
          <div className="relative overflow-hidden rounded-xl bg-primary text-white p-6">
            <div className="absolute inset-0 hero-grid opacity-40" />
            <p className="relative text-sm text-slate-300" data-testid="contact-note">{note}</p>
          </div>
        </aside>
      </section>
    </div>
  );
}
